import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, useTheme, Topbar, PageHeader, LoadingScreen, EmptyState, StatusBadge } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const PER_PAGE = 12;
const STATUSES = ['all', 'pending', 'verified', 'resolved', 'rejected', 'escalated'];
const STATUS_COLOR = { all: COLORS.purple, pending: COLORS.amber, verified: COLORS.blue, resolved: COLORS.green, rejected: COLORS.red, escalated: COLORS.orange };

// Live countdown for pending complaints — shows time left for admin to verify
function VerificationTimer({ deadline }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  if (!deadline) return null;
  const deadlineMs = new Date(deadline.endsWith('Z') ? deadline : deadline + 'Z').getTime();
  const diff = deadlineMs - now;
  const isOverdue = diff < 0;
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const label = h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  const color = isOverdue ? '#ef4444' : diff < 3600000 ? '#f59e0b' : '#10b981';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8,
      padding: '5px 10px', borderRadius: 8,
      background: isOverdue ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.06)',
      border: `1px solid ${color}44` }}>
      <span style={{ fontSize: 12 }}>{isOverdue ? '⚠️' : '⏱'}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
        {isOverdue ? `Overdue by ${label}` : `Admin review in ${label}`}
      </span>
    </div>
  );
}

function ComplaintListInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const { user } = useAuth();

  const isOwn = user?.role === 'student' || user?.role === 'faculty';

  const [allComplaints, setAllComplaints] = useState([]);   // full list for own users
  const [complaints, setComplaints] = useState([]);          // paginated for admin/authority
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeTab, setTypeTab] = useState('dept');            // 'dept' | 'uni' — own users only
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => { setPage(1); }, [statusFilter, search, typeTab]);

  const fetchComplaints = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      if (isOwn) {
        const res = await api.get(`/dashboard/user/${user.id}/complaints`);
        setAllComplaints(Array.isArray(res.data) ? res.data : []);
      } else {
        let url = `/complaints/?page=${page}&per_page=${PER_PAGE}&status=${statusFilter === 'all' ? 'all' : statusFilter}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        const res = await api.get(url);
        setComplaints(res.data.complaints || []);
        setTotalPages(res.data.pages || 1);
        setTotal(res.data.total || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.id, isOwn, page, statusFilter, search]);

  useEffect(() => { fetchComplaints(); }, [fetchComplaints]);

  // Derive filtered list for own users
  const deptAll = allComplaints.filter(c => c.complaint_type === 'department');
  const uniAll  = allComplaints.filter(c => c.complaint_type === 'university');
  const typeList = typeTab === 'dept' ? deptAll : uniAll;

  const filtered = typeList.filter(c => {
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !search || c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const displayList = isOwn ? filtered : complaints;
  const displayTotal = isOwn ? filtered.length : total;

  const tabStyle = (active, color = COLORS.blue) => ({
    padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${active ? color + '88' : 'rgba(255,255,255,0.2)'}`,
    background: active ? color + '25' : 'rgba(255,255,255,0.08)',
    color: active ? color : 'rgba(255,255,255,0.75)',
    transition: 'all 0.18s ease',
  });

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title={isOwn ? 'My Complaints' : 'All Complaints'}
            subtitle={isOwn ? 'Your submitted complaints' : 'Browse and track complaints'}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={<button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/complaints/new')}>+ New</button>}
          />
          <div style={s.content}>
            <PageHeader
              icon="📋"
              title={isOwn ? 'My Complaints' : 'All Complaints'}
              subtitle={isOwn ? 'Complaints you have submitted' : 'Browse and track all complaints'}
              stats={[
                { label: 'Total', value: isOwn ? allComplaints.length : total },
                { label: 'Showing', value: displayList.length },
              ]}
              actions={<button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/complaints/new')}>+ New Complaint</button>}
            />

            {/* Type tabs — own users only */}
            {isOwn && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button style={tabStyle(typeTab === 'dept', COLORS.blue)} onClick={() => setTypeTab('dept')}>
                  Department Complaints ({deptAll.length})
                </button>
                <button style={tabStyle(typeTab === 'uni', COLORS.purple)} onClick={() => setTypeTab('uni')}>
                  University-Wide ({uniAll.length})
                </button>
              </div>
            )}

            {/* Search */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && setSearch(searchInput)}
                placeholder="Search complaints..." style={{ ...s.input, flex: 1 }} />
              <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => setSearch(searchInput)}>Search</button>
              {search && <button className="ur-btn-hover" style={s.btnDanger} onClick={() => { setSearch(''); setSearchInput(''); }}>Clear</button>}
            </div>

            {/* Status filters */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {STATUSES.map(f => (
                <button key={f} style={tabStyle(statusFilter === f, STATUS_COLOR[f])} onClick={() => setStatusFilter(f)}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>

            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
                <div style={s.spinner} />
              </div>
            ) : !displayList.length ? (
              <EmptyState
                text={isOwn
                  ? (typeTab === 'dept' ? 'No department complaints' : 'No university-wide complaints')
                  : 'No complaints found'}
                sub={statusFilter !== 'all' ? `No ${statusFilter} complaints` : 'Nothing here yet'}
              />
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16, marginBottom: 20 }}>
                  {displayList.map((c, i) => {
                    const overdue = c.is_overdue || c.status === 'escalated';
                    const isUni = c.complaint_type === 'university';
                    return (
                      <div key={c.id} className="ur-card-hover" style={{
                        ...s.card, cursor: 'pointer', animationDelay: `${i * 0.04}s`, position: 'relative',
                        borderColor: overdue ? 'rgba(239,68,68,0.5)' : 'rgba(255,255,255,0.22)',
                        background: overdue ? 'rgba(239,68,68,0.12)' : s.card.background,
                      }} onClick={() => navigate(`/complaints/${c.id}`)}>
                        {overdue && (
                          <div style={{
                            position: 'absolute', top: -10, right: 12,
                            padding: '3px 10px', background: COLORS.red, color: '#fff',
                            borderRadius: 20, fontSize: 11, fontWeight: 700,
                            boxShadow: `0 2px 8px ${COLORS.red}55`,
                          }}>OVERDUE</div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: overdue ? '#fca5a5' : '#fff', flex: 1, marginRight: 10 }}>
                            {c.title}
                          </div>
                          <StatusBadge status={c.status} />
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 12, lineHeight: 1.5 }}>
                          {c.description?.slice(0, 100)}{c.description?.length > 100 ? '...' : ''}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {/* Type badge */}
                            <span style={{
                              fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 600,
                              background: isUni ? 'rgba(139,92,246,0.2)' : 'rgba(6,182,212,0.2)',
                              color: isUni ? '#c4b5fd' : '#67e8f9',
                            }}>
                              {isUni ? 'University' : 'Department'}
                            </span>
                            {/* Dept / Category */}
                            <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 5, background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}>
                              {c.department || c.university_category || 'University-Wide'}
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                            {c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 8 }}>
                          By: {isOwn ? user?.name : (c.student_name || 'Unknown')}
                        </div>
                        {isOwn && c.status === 'pending' && c.verification_deadline && (
                          <VerificationTimer deadline={c.verification_deadline} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {!isOwn && totalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '16px 0' }}>
                    <button className="ur-btn-hover" style={{ ...s.btnSecondary, opacity: page === 1 ? 0.4 : 1 }} disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>Page {page} of {totalPages} · {displayTotal} total</span>
                    <button className="ur-btn-hover" style={{ ...s.btnSecondary, opacity: page === totalPages ? 0.4 : 1 }} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ComplaintList() {
  return <ThemeProvider><ComplaintListInner /></ThemeProvider>;
}
